import type { ReactNode } from "react";

import { Box, Icon } from "@mui/material";
import { useTranslation } from "react-i18next";

import { RetryButton } from "~/components/Buttons";
import { PHONE_TOUCH_TARGET_SX } from "~/components/mobile/bottomNav/mobileBottomNav";
import { BodyText, BodyTextSmall } from "~/components/Typographies";

interface RetryableErrorStateProps {
	dense?: boolean;
	icon?: string;
	message?: string;
	onRetry?: () => void;
	retryDisabled?: boolean;
	retryLabel?: string;
	secondaryAction?: ReactNode;
	title?: string;
}

const RetryableErrorState = ({
	dense = false,
	icon = "error_outline",
	message,
	onRetry,
	retryDisabled = false,
	retryLabel,
	secondaryAction,
	title
}: RetryableErrorStateProps) => {
	const { t } = useTranslation("common");
	const resolvedMessage = message ?? t("shell.retryableError.message");
	const resolvedTitle = title ?? t("shell.retryableError.title");

	return (
		<Box
			sx={{
				alignItems: "center",
				color: "text.secondary",
				display: "flex",
				flexDirection: dense ? "row" : "column",
				gap: dense ? 1 : 0,
				p: dense ? 2 : 4,
				textAlign: dense ? "left" : "center"
			}}
		>
			<Icon sx={{ color: "error.main", fontSize: dense ? 24 : 48, mb: dense ? 0 : 1 }}>{icon}</Icon>
			{resolvedTitle && <BodyText sx={{ color: "text.primary" }}>{resolvedTitle}</BodyText>}
			{resolvedMessage && (
				<BodyTextSmall sx={{ color: "text.secondary", mt: dense ? 0 : 0.5 }}>{resolvedMessage}</BodyTextSmall>
			)}
			{(onRetry || secondaryAction) && (
				<Box
					sx={{
						...(secondaryAction ? { alignItems: "center", display: "flex", gap: 1 } : {}),
						ml: dense ? "auto" : 0,
						mt: dense ? 0 : 2
					}}
				>
					{onRetry && (
						<RetryButton disabled={retryDisabled} onClick={onRetry} size="small" sx={PHONE_TOUCH_TARGET_SX}>
							{retryLabel}
						</RetryButton>
					)}
					{secondaryAction}
				</Box>
			)}
		</Box>
	);
};

export default RetryableErrorState;
